import { useEffect, useState } from "react";
import type * as Y from "yjs";
import {
  LOCAL_ORIGIN,
  computePanelLayout,
  listPanels,
  listRows,
  listSections,
  type Panel,
  type PanelLayoutReport,
  type PanelLoadReport,
  type PanelMaps,
  type PanelRow,
  type PanelWarning,
  type Section,
  summarizePanelLoad,
  validatePanelRows,
} from "@electro-planner/domain";
import { getRowModules } from "@electro-planner/components";

export type PanelProjection = {
  panel: Panel | undefined;
  sections: ReadonlyArray<Section>;
  rows: ReadonlyArray<PanelRow>;
  warnings: ReadonlyArray<PanelWarning>;
  load: PanelLoadReport;
  layout: PanelLayoutReport;
};

/**
 * Speglar hela Yjs-världen för en panel till React-state. Vi lyssnar på alla
 * tre Y.Maps (panels/sections/rows) och räknar om projektionen vid varje
 * lokal och inkommande update.
 *
 * Enkel "rerun-all"-strategi funkar här eftersom en panel har ca 40-200 rader.
 * Om det någon gång blir tusentals rader kan vi byta till ett memoiserat
 * delta-projektions-approach.
 */
export function usePanelProjection(
  doc: Y.Doc,
  maps: PanelMaps,
): PanelProjection {
  const [state, setState] = useState<PanelProjection>(() => build(maps));

  useEffect(() => {
    const rebuild = () => setState(build(maps));

    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      // Bygger om även för lokala mutations (LOCAL_ORIGIN) — UI:t ska se
      // resultatet av sina egna skrivningar direkt.
      void origin;
      rebuild();
    };

    doc.on("update", onUpdate);
    rebuild();
    return () => {
      doc.off("update", onUpdate);
    };
  }, [doc, maps]);

  // Suppress unused warning for LOCAL_ORIGIN import (reserveras för framtida
  // filtrering av remote-updates i kollaborationsläge).
  void LOCAL_ORIGIN;

  return state;
}

function build(maps: PanelMaps): PanelProjection {
  const panels = listPanels(maps);
  const panel = panels[0];
  const sections = listSections(maps);
  const rows = listRows(maps);
  const warnings = validatePanelRows(rows, sections);
  const load = summarizePanelLoad(panel, sections, rows);
  const layout = computePanelLayout(panel, rows, getRowModules);
  return { panel, sections, rows, warnings, load, layout };
}
